import React from 'react';
import { useTranslation } from 'react-i18next';

import * as Styled from '../LoyaltyPageSeparated.styles';

export type ScreenType =
  | 'client'
  | 'receipt'
  | 'apply'
  | 'payment'
  | 'processing';

interface LoyaltyTabsProps {
  currentScreen: ScreenType;
  onScreenChange: (screen: ScreenType) => void;
  clientAdded: boolean;
  receiptChosen: boolean;
  orderConfirmed: boolean;
  isPartnersPayment: boolean;
  paymentMethodChosen: boolean;
}

const LoyaltyTabs: React.FC<LoyaltyTabsProps> = ({
  currentScreen,
  onScreenChange,
  clientAdded,
  receiptChosen,
  orderConfirmed,
  isPartnersPayment,
  paymentMethodChosen,
}) => {
  const { t } = useTranslation();

  const tabs: { key: ScreenType; label: string; step: number }[] = [
    {
      key: 'client',
      label: t('LoyaltySeparated.TAB_CLIENT'),
      step: 1,
    },
    {
      key: 'receipt',
      label: t('LoyaltySeparated.TAB_RECEIPT'),
      step: 2,
    },
    {
      key: 'apply',
      label: t('LoyaltySeparated.TAB_CONFIRM'),
      step: 3,
    },
    ...(isPartnersPayment
      ? [
          {
            key: 'payment' as ScreenType,
            label: t('LoyaltySeparated.TAB_PAYMENT'),
            step: 4,
          },
          {
            key: 'processing' as ScreenType,
            label: t('LoyaltySeparated.TAB_PROCESSING'),
            step: 5,
          },
        ]
      : []),
  ];

  const isCompleted = (key: ScreenType) => {
    if (key === 'client') return clientAdded;
    if (key === 'receipt') return receiptChosen;
    if (key === 'apply') return orderConfirmed;
    if (key === 'payment') return paymentMethodChosen;
    return false;
  };

  const isDisabled = (key: ScreenType) => {
    if (key === 'client') return false;
    if (key === 'receipt') return !clientAdded;
    // apply — только если клиент добавлен И чек выбран
    if (key === 'apply') return !clientAdded || !receiptChosen;
    if (key === 'payment')
      return !clientAdded || !receiptChosen || !orderConfirmed;
    // processing — только после того, как выбран способ оплаты
    if (key === 'processing')
      return (
        !clientAdded ||
        !receiptChosen ||
        !orderConfirmed ||
        !paymentMethodChosen
      );
    return false;
  };

  return (
    <Styled.TabsContainer>
      {tabs.map((tab, index) => (
        <React.Fragment key={tab.key}>
          {index > 0 && (
            <Styled.TabConnector completed={isCompleted(tabs[index - 1].key)} />
          )}
          <Styled.TabItem
            active={currentScreen === tab.key}
            disabled={isDisabled(tab.key)}
            onClick={() => {
              if (!isDisabled(tab.key)) {
                onScreenChange(tab.key);
              }
            }}
          >
            <Styled.TabStepNumber
              active={currentScreen === tab.key}
              completed={isCompleted(tab.key)}
              disabled={isDisabled(tab.key)}
            >
              {isCompleted(tab.key) ? '✓' : tab.step}
            </Styled.TabStepNumber>
            {tab.label}
          </Styled.TabItem>
        </React.Fragment>
      ))}
    </Styled.TabsContainer>
  );
};

export default LoyaltyTabs;
